/**
 * Flow Templates Library
 * Pre-built flow templates that users can import to get started quickly
 */

export interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  category: 'regularization' | 'customer-service' | 'survey' | 'notification' | 'other';
  icon: string;
  flow: {
    version: string;
    name: string;
    description: string;
    isActive: boolean;
    isDefault: boolean;
    nodes: any[];
    edges: any[];
  };
}

export const FLOW_TEMPLATES: FlowTemplate[] = [
  {
    id: 'regularization-basic',
    name: 'Regularização Básica',
    description: 'Fluxo simples para envio de template de regularização e aguardar resposta do cliente',
    category: 'regularization',
    icon: '📋',
    flow: {
      version: '1.0',
      name: 'Regularização Básica',
      description: 'Fluxo básico para regularização de assinatura',
      isActive: true,
      isDefault: false,
      nodes: [
        {
          id: 'start-1',
          type: 'custom',
          position: { x: 250, y: 50 },
          data: {
            label: 'Início',
            nodeType: 'START',
            config: {},
          },
        },
        {
          id: 'send-template-1',
          type: 'custom',
          position: { x: 250, y: 150 },
          data: {
            label: 'Enviar Template',
            nodeType: 'SEND_TEMPLATE',
            config: {
              templateName: 'regularizacao_cobranca',
              templateVariables: [
                { key: '1', value: '{{customer_name}}' },
                { key: '2', value: '{{dias_cancelamento}}' },
              ],
            },
          },
        },
        {
          id: 'wait-1',
          type: 'custom',
          position: { x: 250, y: 250 },
          data: {
            label: 'Aguardar Resposta',
            nodeType: 'WAIT_RESPONSE',
            config: {},
          },
        },
        {
          id: 'update-status-1',
          type: 'custom',
          position: { x: 250, y: 350 },
          data: {
            label: 'Atualizar Status',
            nodeType: 'UPDATE_STATUS',
            config: {
              status: 'ANSWERED',
            },
          },
        },
        {
          id: 'end-1',
          type: 'custom',
          position: { x: 250, y: 450 },
          data: {
            label: 'Fim',
            nodeType: 'END',
            config: {},
          },
        },
      ],
      edges: [
        {
          id: 'e1-2',
          source: 'start-1',
          target: 'send-template-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e2-3',
          source: 'send-template-1',
          target: 'wait-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e3-4',
          source: 'wait-1',
          target: 'update-status-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e4-5',
          source: 'update-status-1',
          target: 'end-1',
          type: 'smoothstep',
          animated: true,
        },
      ],
    },
  },
  {
    id: 'customer-service-conditional',
    name: 'Atendimento com Decisão',
    description: 'Fluxo com condições para direcionar o cliente baseado em sua resposta',
    category: 'customer-service',
    icon: '🔀',
    flow: {
      version: '1.0',
      name: 'Atendimento com Decisão',
      description: 'Fluxo com decisões baseadas em respostas do cliente',
      isActive: true,
      isDefault: false,
      nodes: [
        {
          id: 'start-1',
          type: 'custom',
          position: { x: 400, y: 50 },
          data: {
            label: 'Início',
            nodeType: 'START',
            config: {},
          },
        },
        {
          id: 'send-msg-1',
          type: 'custom',
          position: { x: 400, y: 150 },
          data: {
            label: 'Pergunta Inicial',
            nodeType: 'SEND_MESSAGE',
            config: {
              message: 'Olá {{customer_name}}! Você deseja regularizar sua assinatura? Responda SIM ou NÃO.',
            },
          },
        },
        {
          id: 'wait-1',
          type: 'custom',
          position: { x: 400, y: 250 },
          data: {
            label: 'Aguardar Resposta',
            nodeType: 'WAIT_RESPONSE',
            config: {},
          },
        },
        {
          id: 'condition-1',
          type: 'custom',
          position: { x: 400, y: 350 },
          data: {
            label: 'Verificar Resposta',
            nodeType: 'CONDITION',
            config: {},
          },
        },
        {
          id: 'transfer-1',
          type: 'custom',
          position: { x: 200, y: 500 },
          data: {
            label: 'Transferir Financeiro',
            nodeType: 'TRANSFER_DEPARTMENT',
            config: {
              departmentId: '',
              departmentName: 'Financeiro',
              assignToAgent: true,
            },
          },
        },
        {
          id: 'send-msg-2',
          type: 'custom',
          position: { x: 600, y: 500 },
          data: {
            label: 'Agradecimento',
            nodeType: 'SEND_MESSAGE',
            config: {
              message: 'Obrigado pelo retorno! Em caso de dúvidas, estamos à disposição.',
            },
          },
        },
        {
          id: 'close-1',
          type: 'custom',
          position: { x: 200, y: 650 },
          data: {
            label: 'Encerrar - Transferido',
            nodeType: 'END',
            config: {},
          },
        },
        {
          id: 'close-2',
          type: 'custom',
          position: { x: 600, y: 650 },
          data: {
            label: 'Encerrar - Recusado',
            nodeType: 'CLOSE_CONVERSATION',
            config: {
              reason: 'Cliente recusou regularização',
            },
          },
        },
      ],
      edges: [
        {
          id: 'e1-2',
          source: 'start-1',
          target: 'send-msg-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e2-3',
          source: 'send-msg-1',
          target: 'wait-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e3-4',
          source: 'wait-1',
          target: 'condition-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e4-5',
          source: 'condition-1',
          target: 'transfer-1',
          type: 'smoothstep',
          animated: true,
          label: 'Se SIM',
          data: {
            condition: {
              field: 'last_response',
              operator: 'contains',
              value: 'sim',
            },
            priority: 1,
          },
        },
        {
          id: 'e4-6',
          source: 'condition-1',
          target: 'send-msg-2',
          type: 'smoothstep',
          animated: true,
          label: 'Se NÃO',
          data: {
            condition: {
              field: 'last_response',
              operator: 'contains',
              value: 'não',
            },
            priority: 0,
          },
        },
        {
          id: 'e5-7',
          source: 'transfer-1',
          target: 'close-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e6-8',
          source: 'send-msg-2',
          target: 'close-2',
          type: 'smoothstep',
          animated: true,
        },
      ],
    },
  },
  {
    id: 'survey-feedback',
    name: 'Pesquisa de Satisfação',
    description: 'Fluxo para coletar feedback do cliente com múltiplas perguntas',
    category: 'survey',
    icon: '📊',
    flow: {
      version: '1.0',
      name: 'Pesquisa de Satisfação',
      description: 'Coleta feedback em múltiplas etapas',
      isActive: true,
      isDefault: false,
      nodes: [
        {
          id: 'start-1',
          type: 'custom',
          position: { x: 250, y: 50 },
          data: {
            label: 'Início',
            nodeType: 'START',
            config: {},
          },
        },
        {
          id: 'send-msg-1',
          type: 'custom',
          position: { x: 250, y: 150 },
          data: {
            label: 'Pergunta 1',
            nodeType: 'SEND_MESSAGE',
            config: {
              message: 'Olá {{customer_name}}! Como você avalia nosso atendimento? (1-5)',
            },
          },
        },
        {
          id: 'wait-1',
          type: 'custom',
          position: { x: 250, y: 250 },
          data: {
            label: 'Aguardar Nota',
            nodeType: 'WAIT_RESPONSE',
            config: {},
          },
        },
        {
          id: 'set-var-1',
          type: 'custom',
          position: { x: 250, y: 350 },
          data: {
            label: 'Salvar Nota',
            nodeType: 'SET_VARIABLE',
            config: {
              variableName: 'nota_atendimento',
              value: '{{last_response}}',
            },
          },
        },
        {
          id: 'send-msg-2',
          type: 'custom',
          position: { x: 250, y: 450 },
          data: {
            label: 'Pergunta 2',
            nodeType: 'SEND_MESSAGE',
            config: {
              message: 'Obrigado! Você recomendaria nossos serviços? (SIM/NÃO)',
            },
          },
        },
        {
          id: 'wait-2',
          type: 'custom',
          position: { x: 250, y: 550 },
          data: {
            label: 'Aguardar Recomendação',
            nodeType: 'WAIT_RESPONSE',
            config: {},
          },
        },
        {
          id: 'send-msg-3',
          type: 'custom',
          position: { x: 250, y: 650 },
          data: {
            label: 'Agradecimento Final',
            nodeType: 'SEND_MESSAGE',
            config: {
              message: 'Muito obrigado pelo seu feedback! Ele é muito importante para nós.',
            },
          },
        },
        {
          id: 'close-1',
          type: 'custom',
          position: { x: 250, y: 750 },
          data: {
            label: 'Encerrar',
            nodeType: 'CLOSE_CONVERSATION',
            config: {
              reason: 'Pesquisa concluída',
            },
          },
        },
      ],
      edges: [
        {
          id: 'e1-2',
          source: 'start-1',
          target: 'send-msg-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e2-3',
          source: 'send-msg-1',
          target: 'wait-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e3-4',
          source: 'wait-1',
          target: 'set-var-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e4-5',
          source: 'set-var-1',
          target: 'send-msg-2',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e5-6',
          source: 'send-msg-2',
          target: 'wait-2',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e6-7',
          source: 'wait-2',
          target: 'send-msg-3',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e7-8',
          source: 'send-msg-3',
          target: 'close-1',
          type: 'smoothstep',
          animated: true,
        },
      ],
    },
  },
  {
    id: 'notification-simple',
    name: 'Notificação Simples',
    description: 'Fluxo para envio de notificação/lembrete sem necessidade de resposta',
    category: 'notification',
    icon: '🔔',
    flow: {
      version: '1.0',
      name: 'Notificação Simples',
      description: 'Envio de notificação sem aguardar resposta',
      isActive: true,
      isDefault: false,
      nodes: [
        {
          id: 'start-1',
          type: 'custom',
          position: { x: 250, y: 50 },
          data: {
            label: 'Início',
            nodeType: 'START',
            config: {},
          },
        },
        {
          id: 'send-template-1',
          type: 'custom',
          position: { x: 250, y: 150 },
          data: {
            label: 'Enviar Notificação',
            nodeType: 'SEND_TEMPLATE',
            config: {
              templateName: 'lembrete_vencimento',
              templateVariables: [
                { key: '1', value: '{{customer_name}}' },
                { key: '2', value: '{{data_vencimento}}' },
              ],
            },
          },
        },
        {
          id: 'update-status-1',
          type: 'custom',
          position: { x: 250, y: 250 },
          data: {
            label: 'Marcar como Enviado',
            nodeType: 'UPDATE_STATUS',
            config: {
              status: 'CLOSED',
            },
          },
        },
        {
          id: 'end-1',
          type: 'custom',
          position: { x: 250, y: 350 },
          data: {
            label: 'Fim',
            nodeType: 'CLOSE_CONVERSATION',
            config: {
              reason: 'Notificação enviada',
            },
          },
        },
      ],
      edges: [
        {
          id: 'e1-2',
          source: 'start-1',
          target: 'send-template-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e2-3',
          source: 'send-template-1',
          target: 'update-status-1',
          type: 'smoothstep',
          animated: true,
        },
        {
          id: 'e3-4',
          source: 'update-status-1',
          target: 'end-1',
          type: 'smoothstep',
          animated: true,
        },
      ],
    },
  },
];

export const getTemplatesByCategory = (category: string): FlowTemplate[] => {
  return FLOW_TEMPLATES.filter((t) => t.category === category);
};

export const getTemplateById = (id: string): FlowTemplate | undefined => {
  return FLOW_TEMPLATES.find((t) => t.id === id);
};
